import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getGame } from '../services/games';
import { GameWithPlayers } from '../types';
import { supabase } from '../lib/supabase';
import { getPlayerDisplayName } from '../utils/playerDisplay';
import Loading from '../components/Loading';
import './GameDetail.css';

const GameDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameWithPlayers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actualFramesPlayerA, setActualFramesPlayerA] = useState(0);
  const [actualFramesPlayerB, setActualFramesPlayerB] = useState(0);

  useEffect(() => {
    if (id) {
      loadGame(parseInt(id));
    }
  }, [id]);

  const loadGame = async (gameId: number) => {
    try {
      setLoading(true);
      setError(null);
      const gameData = await getGame(gameId);
      setGame(gameData);
      
      // Calculate actual frame scores from completed frames
      await calculateActualFrameScores(gameId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const calculateActualFrameScores = async (gameId: number) => {
    try {
      // Load all completed frames to calculate actual scores
      const { data: allFrames, error: framesError } = await supabase
        .from('frames')
        .select('*')
        .eq('gameId', gameId)
        .not('playerAFinalScore', 'is', null)
        .not('playerBFinalScore', 'is', null)
        .order('created_at', { ascending: true });

      if (framesError) {
        console.error('Error loading completed frames:', framesError);
        return;
      }

      if (allFrames) {
        // Calculate actual frame counts based on completed frames
        const actualPlayerA = allFrames.filter(f => f.playerAFinalScore > f.playerBFinalScore).length;
        const actualPlayerB = allFrames.filter(f => f.playerBFinalScore > f.playerAFinalScore).length;
        
        setActualFramesPlayerA(actualPlayerA);
        setActualFramesPlayerB(actualPlayerB);
        
        console.log('GameDetail - Calculated actual scores:', {
          actualPlayerA,
          actualPlayerB,
          totalFrames: allFrames.length
        });
      }
    } catch (err) {
      console.error('Failed to calculate actual frame scores:', err);
    }
  };

  if (loading) return <Loading />;

  if (error || !game) {
    return (
      <div className="game-detail-container">
        <div className="error-message">
          <p>{error || 'Game not found'}</p>
          <button onClick={() => navigate('/games')} className="btn btn-secondary">
            Back to Games
          </button>
        </div>
      </div>
    );
  }

  const isFinished = (() => {
    // Game is finished when one player has won more than half of the frames
    const totalFramesPlayed = actualFramesPlayerA + actualFramesPlayerB;
    const framesToWin = Math.ceil(game.framesToPlay / 2);
    
    return actualFramesPlayerA >= framesToWin || 
           actualFramesPlayerB >= framesToWin || 
           totalFramesPlayed >= game.framesToPlay;
  })();

  return (
    <div className="game-detail-container">
      <div className="game-header">
        <button onClick={() => navigate('/games')} className="btn btn-secondary">
          ← Back to Games
        </button>
        <h1>Game Details</h1>
      </div>

      <div className="game-info">
        <div className="game-players">
          <h2>
            <span className={game.playerA.is_anonymous ? 'anonymous-player' : 'regular-player'}>
              {getPlayerDisplayName(game.playerA)}
            </span>
            {' vs '}
            <span className={game.playerB.is_anonymous ? 'anonymous-player' : 'regular-player'}>
              {getPlayerDisplayName(game.playerB)}
            </span>
          </h2>
        </div>
        
        <div className="game-score">
          <div className="score-item">
            <span className="score-value">{actualFramesPlayerA}</span>
          </div>
          <div className="score-separator">:</div>
          <div className="score-item">
            <span className="score-value">{actualFramesPlayerB}</span>
          </div>
        </div>

        <div className="game-status">
          <p>Frames to play: {game.framesToPlay}</p>
          <p>Status: {isFinished ? 'Finished' : 'In Progress'}</p>
        </div>
      </div>

      <div className="game-actions">
        <button 
          onClick={() => navigate(`/play/${game.id}`)}
          className="btn btn-primary"
        >
          {isFinished ? t('common.play') : t('games.continueGame')}
        </button>
      </div>
    </div>
  );
};

export default GameDetail;

